"use client";
import HeroSearch from "./HeroSearch";
import { LazyMotion, m, domAnimation } from "framer-motion";
import Image from "next/image";
import React from "react";
import { useResponsiveWidth } from "src/app/components/hooks/useWindowWidth";
import {
  AnimatedHouse,
  AnimatedHouse1,
  AnimatedHouse2,
  AnimatedHouse3,
} from "src/app/components/Icon";

const Hero = () => {
  const { isMobile } = useResponsiveWidth();
  return (
    <LazyMotion features={domAnimation}>
      <section
        className="relative"
        style={{
          backgroundColor: "rgba(var(--primary-color-rgb), 0.1)",
        }}
      >
        <AnimatedIcon
          Icon={AnimatedHouse}
          top="18%"
          left={isMobile ? "8%" : "50%"}
          distance={100}
          animation="custom"
          duration={40}
        />

        <AnimatedIcon
          Icon={AnimatedHouse1}
          top="80%"
          left={isMobile ? "0%" : "2%"}
          animation="float"
          duration={30}
        />

        <AnimatedIcon
          Icon={AnimatedHouse2}
          top="30%"
          left={isMobile ? "5%" : "40%"}
          animation="zigzag"
          duration={8}
          speed={50}
        />

        <AnimatedIcon
          Icon={AnimatedHouse3}
          top="10%"
          left={isMobile ? "20%" : "30%"}
          animation="float"
          duration={8}
          speed={50}
        />
        <div className="w-full flex justify-end">
          <Image
            height={500}
            width={500}
            priority
            className="bg-cover w-[65%] hidden sm:block"
            src="/images/property-frame-2.webp"
            alt="bottom-image"
          />
        </div>

        <Image
          height={500}
          width={500}
          priority
          className="bg-cover sm:hidden xs:h-[45vh] xs:min-h-[45vh] xs:top-0 min-h-full absolute block size-[55vw] h-[43vw] -right-10 bottom-0"
          src="/images/property-frame.webp"
          alt="bottom-image"
        />
        <div className="wrapper sm:pt-[1rem] py-[8rem] relative pb-[11.5rem]  ">
          <Image
            height={400}
            width={400}
            className=" absolute w-[500px] bottom-0"
            src="/images/bottom-image.webp"
            alt="bottom-image"
          />
          <h1
            className={`text-secondary-main max-w-[600px] leading-10 mb-12 ${
              isMobile ? "h3" : "h1"
            }`}
          >
            Explore The Perfect Properties For Your Interests & Needs
          </h1>
          <div className="first"></div>
          <HeroSearch />
        </div>
      </section>
    </LazyMotion>
  );
};
export default Hero;

type AnimationType = "float" | "spin" | "zigzag" | "custom";

interface AnimatedIconProps {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  top: string;
  left: string;
  animation?: AnimationType;
  duration?: number;
  speed?: number;
  distance?: number;
}

export const AnimatedIcon: React.FC<AnimatedIconProps> = ({
  Icon,
  top,
  left,
  animation = "float",
  duration = 5,
  speed = 30,
  distance = 50, // Added default distance for "custom"
}) => {
  const animations = {
    float: {
      y: [0, -speed, 0], // Moves up and back
      transition: { duration, repeat: Infinity, ease: "easeInOut" },
    },
    spin: {
      rotate: [0, 360], // Rotates infinitely
      transition: { duration, repeat: Infinity, ease: "linear" },
    },
    zigzag: {
      x: [-speed / 2, speed / 2, -speed / 2], // Zigzag left & right
      y: [0, speed / 2, -speed / 2], // Moves up & down
      transition: { duration, repeat: Infinity, ease: "easeInOut" },
    },
    custom: {
      y: [0, -distance, 0, 0, 0], // Move up → Return → Hold
      x: [0, 0, 0, -distance, 0], // Hold → Move left → Return
      transition: { duration, repeat: Infinity, ease: "easeInOut" },
    },
  };

  return (
    <m.div
      className="absolute"
      style={{ top, left }}
      animate={animations[animation]}
    >
      <Icon className="w-8 h-8 text-themeColor" />
    </m.div>
  );
};
